// Shopify App Bridge utilities for embedded app authentication
// Uses session tokens instead of cookies for secure authentication
// Required: App Bridge CDN script loaded with API key meta tag in index.html

declare global {
    interface Window {
        shopify?: {
            config: {
                apiKey: string;
                host: string;
                shop: string;
            };
            idToken: () => Promise<string>;
            toast: {
                show: (message: string, options?: { duration?: number }) => void;
            };
            loading: (isLoading: boolean) => void;
        };
        __SHOPIFY_API_KEY__?: string;
    }
}

// Get shop and host from URL parameters (Shopify injects these when loading embedded app)
export function getShopifyParams(): { shop: string | null; host: string | null; apiKey: string | null } {
    const urlParams = new URLSearchParams(window.location.search);
    return {
        shop: urlParams.get('shop'),
        host: urlParams.get('host'),
        apiKey: urlParams.get('apiKey') || window.__SHOPIFY_API_KEY__ || null,
    };
}

// Check if we're running inside Shopify Admin (embedded)
export function isEmbedded(): boolean {
    if (typeof window === 'undefined') return false;

    // Method 1: Check if we're in an iframe
    const isInIframe = window.location !== window.parent.location;

    // Method 2: Check for Shopify URL parameters
    const { shop, host } = getShopifyParams();
    const hasShopifyParams = !!(shop && host);

    // Method 3: Check if App Bridge is available
    const hasAppBridge = !!window.shopify;

    const embedded = isInIframe || hasShopifyParams || hasAppBridge;

    if (embedded) {
        console.log('🛒 Running in Shopify embedded context', {
            isInIframe,
            hasShopifyParams,
            hasAppBridge,
            shop: shop?.substring(0, 20),
        });
    }

    return embedded;
}

// Wait for App Bridge to be available
async function waitForAppBridge(timeoutMs: number = 5000): Promise<boolean> {
    const startTime = Date.now();
    const checkInterval = 100;

    while (Date.now() - startTime < timeoutMs) {
        if (window.shopify?.idToken) {
            console.log('✅ App Bridge ready after', Date.now() - startTime, 'ms');
            return true;
        }
        await new Promise(r => setTimeout(r, checkInterval));
    }

    console.warn('⏱️ App Bridge timeout after', timeoutMs, 'ms');
    return false;
}

// Get session token for authenticated API calls
// This is the key function for session token authentication required by Shopify's embedded app checks
export async function getSessionToken(): Promise<string | null> {
    // Check if we should even try (are we in embedded context?)
    if (!isEmbedded()) {
        console.log('📱 Not in embedded context, skipping session token');
        return null;
    }

    // Wait for App Bridge to initialize (up to 5 seconds)
    const isReady = await waitForAppBridge(5000);

    if (!isReady || !window.shopify?.idToken) {
        console.warn('❌ App Bridge not available - cannot get session token');
        console.log('Debug info:', {
            windowShopify: !!window.shopify,
            hasIdToken: !!window.shopify?.idToken,
            apiKeyMeta: document.querySelector('meta[name="shopify-api-key"]')?.getAttribute('content')?.substring(0, 8),
            url: window.location.href.substring(0, 100),
        });
        return null;
    }

    try {
        const token = await window.shopify.idToken();
        console.log('🔐 Session token obtained successfully');
        return token;
    } catch (error) {
        console.error('❌ Failed to get session token:', error);
        return null;
    }
}

// Make authenticated API call using session token
export async function authenticatedFetch(
    url: string,
    options: RequestInit = {}
): Promise<Response> {
    const token = await getSessionToken();

    const headers = new Headers(options.headers);

    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        headers.set('X-Shopify-Session-Token', token);
    }

    // Also include shop for backend to identify the store
    const { shop } = getShopifyParams();
    if (shop) {
        headers.set('X-Shopify-Shop', shop);
    }

    return nativeFetch(url, {
        ...options,
        headers,
        credentials: 'include',
    });
}

// Keep legacy direct fetch callers authenticated while the app is embedded.
// The wrapper is installed once at startup; authenticatedFetch itself uses the
// original browser fetch to avoid recursion.
const nativeFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : fetch;
export function installAuthenticatedFetch(): void {
    if (typeof window === 'undefined' || (window as any).__catalogMuseFetchInstalled) return;
    const original = nativeFetch;
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input.toString();
        if (url.includes('/api/') || url.includes('/shopify/')) return authenticatedFetch(input as any, init);
        return original(input, init);
    }) as typeof window.fetch;
    (window as any).__catalogMuseFetchInstalled = true;
}

// Show toast notification in Shopify Admin
export function showToast(message: string, duration: number = 5000): void {
    if (window.shopify?.toast) {
        window.shopify.toast.show(message, { duration });
    } else {
        console.log('Toast:', message);
    }
}

// Show/hide loading indicator in Shopify Admin
export function setLoading(isLoading: boolean): void {
    if (window.shopify?.loading) {
        window.shopify.loading(isLoading);
    }
}

// Diagnostic function to check App Bridge status (useful for debugging)
export function getAppBridgeStatus(): {
    isEmbedded: boolean;
    hasAppBridge: boolean;
    hasIdToken: boolean;
    apiKey: string | null;
    shop: string | null;
    host: string | null;
} {
    const params = getShopifyParams();
    return {
        isEmbedded: isEmbedded(),
        hasAppBridge: !!window.shopify,
        hasIdToken: !!window.shopify?.idToken,
        apiKey: params.apiKey ? params.apiKey.substring(0, 8) + '...' : null,
        shop: params.shop,
        host: params.host ? params.host.substring(0, 20) + '...' : null,
    };
}

// Log App Bridge status on load (for debugging embedded app checks)
if (typeof window !== 'undefined') {
    // Wait a bit for App Bridge to initialize, then log status
    setTimeout(() => {
        if (isEmbedded()) {
            console.log('📊 App Bridge Status:', getAppBridgeStatus());
        }
    }, 1000);
}
